import React, { ReactElement, useMemo } from 'react'
import { useRecoilState } from 'recoil'
import { Modal, Radio, Pagination, Empty, Checkbox } from 'antd'
import {
    isNotificationQueueModalVisibleAtom,
    notificationQueueFilterAtom,
    notificationQueueSortAtom,
    notificationQueuePageAtom,
} from '@/atoms/notifications'
import { useNotificationQueue } from '@/hooks/useNotificationQueue'
import { NotificationQueueItem } from '@/components/NotificationQueueModal/NotificationQueueItem/NotificationQueueItem'
import styles from './NotificationQueueModal.module.less'

export const NotificationQueueModal: React.FC = (): ReactElement => {
    const [isVisible, setIsVisible] = useRecoilState(
        isNotificationQueueModalVisibleAtom
    )
    const [filter, setFilter] = useRecoilState(notificationQueueFilterAtom)
    const [sort, setSort] = useRecoilState(notificationQueueSortAtom)
    const [page, setPage] = useRecoilState(notificationQueuePageAtom)

    const { queueData, toggleComplete, toggleAllOnPage, saveNotes } =
        useNotificationQueue()

    const allOnPageComplete = useMemo(() => {
        if (!queueData?.notifications?.length) return false
        return queueData.notifications.every((n) => n.isCompleted)
    }, [queueData])

    const hasNotifications = !!queueData?.notifications?.length

    return (
        <Modal
            open={isVisible}
            onCancel={() => setIsVisible(false)}
            footer={null}
            width={700}
            destroyOnClose={false}
            style={{ top: 150 }}
        >
            <div>
                <div className={styles.queueModalHeader}>
                    <div className={styles.queueModalTitle}>
                        Notification Queue
                    </div>
                    <div className={styles.queueModalControls}>
                        <span className={styles.controlLabel}>Filter:</span>
                        <Radio.Group
                            size="small"
                            value={filter}
                            onChange={(e) => {
                                setFilter(e.target.value)
                                setPage(1)
                            }}
                        >
                            <Radio.Button value="incomplete">
                                Incomplete
                            </Radio.Button>
                            <Radio.Button value="complete">
                                Complete
                            </Radio.Button>
                            <Radio.Button value="all">All</Radio.Button>
                        </Radio.Group>
                        <span className={styles.controlLabel}>Sort:</span>
                        <Radio.Group
                            size="small"
                            value={sort}
                            onChange={(e) => {
                                setSort(e.target.value)
                                setPage(1)
                            }}
                        >
                            <Radio.Button value="newest">Newest</Radio.Button>
                            <Radio.Button value="oldest">Oldest</Radio.Button>
                        </Radio.Group>
                    </div>
                </div>
                {hasNotifications && (
                    <div className={styles.selectAllRow}>
                        <Checkbox
                            checked={allOnPageComplete}
                            onChange={(e) => toggleAllOnPage(e.target.checked)}
                        >
                            Select All
                        </Checkbox>
                    </div>
                )}
                <div className={styles.queueModalList}>
                    {hasNotifications ? (
                        queueData.notifications.map((notification) => (
                            <NotificationQueueItem
                                key={notification.id}
                                notification={notification}
                                onToggleComplete={toggleComplete}
                                onSaveNotes={saveNotes}
                            />
                        ))
                    ) : (
                        <Empty
                            description={
                                filter === 'incomplete'
                                    ? 'No incomplete notifications'
                                    : 'No notifications'
                            }
                        />
                    )}
                </div>
                {queueData && queueData.totalCount > 20 && (
                    <div className={styles.queueModalPagination}>
                        <Pagination
                            size="small"
                            current={page}
                            total={queueData.totalCount}
                            pageSize={20}
                            onChange={(newPage) => setPage(newPage)}
                            showSizeChanger={false}
                        />
                    </div>
                )}
            </div>
        </Modal>
    )
}
